var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { rustTrim, toAsciiUppercase } from "@/services/rustText";
import { EMessageAttemptState, EMessageChannel, MESSAGE_CHANNELS, VOTER_ATTR_MESSAGE_CHANNEL, VOTER_ATTR_MESSAGE_CONSENT, VOTER_ATTR_MESSAGE_NUMBER, VOTER_ATTR_MESSENGER_ID, VOTER_ATTR_MESSENGER_PAGE, VOTER_ATTR_MOBILE_NUMBER, VOTER_ATTR_VERIFIED_CHANNELS, VOTER_ATTR_VIBER_NUMBER, VOTER_ATTR_WHATSAPP_NUMBER, } from "@/types/messaging";
/** Which shape of record the channel was read from, as `VoterChannelSource` in sequent-core. */
export var EVoterChannelSource;
(function (EVoterChannelSource) {
    /** `message-channel` with its destination. */
    EVoterChannelSource["RECORDED"] = "RECORDED";
    /** A record of the first messaging release (`verified-channels`). */
    EVoterChannelSource["VERIFIED_CHANNELS"] = "VERIFIED_CHANNELS";
    /** A voter enrolled before messaging: the email, else the mobile number. */
    EVoterChannelSource["LEGACY"] = "LEGACY";
})(EVoterChannelSource || (EVoterChannelSource = {}));
// Attributes the drawer shows in its own section, not as editable fields.
var MESSAGING_ATTRIBUTES = [
    VOTER_ATTR_MESSAGE_CHANNEL,
    VOTER_ATTR_MESSAGE_NUMBER,
    VOTER_ATTR_MESSENGER_ID,
    VOTER_ATTR_MESSENGER_PAGE,
    VOTER_ATTR_MESSAGE_CONSENT,
    VOTER_ATTR_WHATSAPP_NUMBER,
    VOTER_ATTR_VIBER_NUMBER,
    VOTER_ATTR_VERIFIED_CHANNELS,
];
export var isVoterMessagingAttribute = function (name) {
    return MESSAGING_ATTRIBUTES.includes(name);
};
/** An attribute's values, as Keycloak holds them: a list. */
var values = function (attributes, name) {
    var value = attributes === null || attributes === void 0 ? void 0 : attributes[name];
    if (value === undefined || value === null)
        return undefined;
    return Array.isArray(value) ? value : [value];
};
/** The attribute's first value, unless it is empty: an empty first value is none. */
var first = function (attributes, name) {
    var _a;
    var value = (_a = values(attributes, name)) === null || _a === void 0 ? void 0 : _a[0];
    return typeof value === "string" && value !== "" ? value : undefined;
};
/**
 * A channel as a record holds it: trimmed and in any (ASCII) case, as
 * `parse_channel_value` reads it. Anything else is no channel.
 */
export var parseChannelValue = function (value) {
    if (typeof value !== "string")
        return undefined;
    var text = toAsciiUppercase(rustTrim(value));
    return MESSAGE_CHANNELS.find(function (channel) { return channel === text; });
};
var messenger = function (attributes) {
    var id = first(attributes, VOTER_ATTR_MESSENGER_ID);
    var page = first(attributes, VOTER_ATTR_MESSENGER_PAGE);
    return id && page ? { destination: id, messengerPage: page } : undefined;
};
var number = function (value) {
    return value ? { destination: value } : undefined;
};
/** The destination of `channel` in a recorded (ticket-shaped) record. */
var recordedDestination = function (channel, email, attributes) {
    var _a;
    switch (channel) {
        case EMessageChannel.WHATSAPP:
        case EMessageChannel.VIBER:
            return number(first(attributes, VOTER_ATTR_MESSAGE_NUMBER));
        case EMessageChannel.SMS:
            // The same channel: the mobile number when there's no message number.
            return number((_a = first(attributes, VOTER_ATTR_MESSAGE_NUMBER)) !== null && _a !== void 0 ? _a : first(attributes, VOTER_ATTR_MOBILE_NUMBER));
        case EMessageChannel.MESSENGER:
            return messenger(attributes);
        case EMessageChannel.EMAIL:
            return number(email);
    }
};
/** The destination of `channel` in a first-release record. */
var verifiedDestination = function (channel, email, attributes) {
    switch (channel) {
        case EMessageChannel.WHATSAPP:
            return number(first(attributes, VOTER_ATTR_WHATSAPP_NUMBER));
        case EMessageChannel.VIBER:
            return number(first(attributes, VOTER_ATTR_VIBER_NUMBER));
        case EMessageChannel.SMS:
            return number(first(attributes, VOTER_ATTR_MOBILE_NUMBER));
        case EMessageChannel.MESSENGER:
            return messenger(attributes);
        case EMessageChannel.EMAIL:
            return number(email);
    }
};
/**
 * The voter's one channel, or undefined when the record has none that can be
 * used. The same rule as `voter_channel` in sequent-core
 * (`src/types/voter_channel.rs`), case for case: its tests are this one's
 * table (R7-05). sequent-core's WASM package doesn't export it, so it is
 * mirrored here. Never another channel than the record's:
 * 1. a first-release record (`verified-channels` with at least one value):
 *    its `message-channel` if verified, else the first verified channel in
 *    channel order that has that release's destination; none, otherwise;
 * 2. `message-channel` with its recorded destination (SMS also reads the
 *    mobile number); none when the destination is missing, never the email;
 * 3. legacy, with neither: the email, else the mobile number. That defines
 *    the voter's one channel; it is not a fallback.
 */
export var voterChannel = function (email, attributes) {
    var address = email ? email : undefined;
    var recorded = parseChannelValue(first(attributes, VOTER_ATTR_MESSAGE_CHANNEL));
    var declared = values(attributes, VOTER_ATTR_VERIFIED_CHANNELS);
    if (declared && declared.length > 0) {
        var verified_1 = declared
            .map(parseChannelValue)
            .filter(function (channel) { return !!channel; });
        var candidates = __spreadArray(__spreadArray([], (recorded && verified_1.includes(recorded) ? [recorded] : []), true), MESSAGE_CHANNELS.filter(function (channel) { return verified_1.includes(channel); }), true);
        for (var _i = 0, candidates_1 = candidates; _i < candidates_1.length; _i++) {
            var channel = candidates_1[_i];
            var found = verifiedDestination(channel, address, attributes);
            if (found) {
                return __assign(__assign({ channel: channel }, found), { source: EVoterChannelSource.VERIFIED_CHANNELS });
            }
        }
        return undefined;
    }
    if (recorded) {
        var found = recordedDestination(recorded, address, attributes);
        return found
            ? __assign(__assign({ channel: recorded }, found), { source: EVoterChannelSource.RECORDED }) : undefined;
    }
    if (address) {
        return {
            channel: EMessageChannel.EMAIL,
            destination: address,
            source: EVoterChannelSource.LEGACY,
        };
    }
    var mobile = first(attributes, VOTER_ATTR_MOBILE_NUMBER);
    return mobile
        ? { channel: EMessageChannel.SMS, destination: mobile, source: EVoterChannelSource.LEGACY }
        : undefined;
};
/** The consent time recorded with the channel (`{at, version, organization, channel}`). */
var consentAt = function (attributes) {
    var raw = first(attributes, VOTER_ATTR_MESSAGE_CONSENT);
    if (!raw)
        return undefined;
    try {
        var consent = JSON.parse(raw);
        return typeof (consent === null || consent === void 0 ? void 0 : consent.at) === "string" && consent.at ? consent.at : undefined;
    }
    catch (_a) {
        return undefined;
    }
};
/** The messaging the voter's Keycloak record holds: one channel and its consent. */
export var voterMessaging = function (email, attributes) { return ({
    channel: voterChannel(email, attributes),
    consentAt: consentAt(attributes),
}); };
var attemptOrder = function (a, b) {
    var _a, _b, _c, _d;
    return ((_a = a.attempt) !== null && _a !== void 0 ? _a : 0) - ((_b = b.attempt) !== null && _b !== void 0 ? _b : 0) ||
        String((_c = a.created_at) !== null && _c !== void 0 ? _c : "").localeCompare(String((_d = b.created_at) !== null && _d !== void 0 ? _d : ""));
};
/**
 * A message is in the state of its latest attempt, as the event tab counts it:
 * a failed attempt that a later attempt of the same message settled (retried,
 * accepted or delivered) is no longer a failure. Unknown is never a failure:
 * it is "delivery not confirmed", and Unresolved after 24 hours.
 *
 * `candidates` are the voter's FAILED and UNKNOWN attempts, latest first;
 * `attempts` every attempt of their messages.
 */
export var voterDeliveryProblems = function (candidates, attempts) {
    var _a;
    var latest = new Map();
    for (var _i = 0, _b = __spreadArray(__spreadArray([], candidates, true), attempts, true); _i < _b.length; _i++) {
        var attempt = _b[_i];
        var key = (_a = attempt.logical_key) !== null && _a !== void 0 ? _a : attempt.id;
        var current = latest.get(key);
        if (!current || attemptOrder(current, attempt) < 0) {
            latest.set(key, attempt);
        }
    }
    var isLatest = function (message) { var _a, _b; return ((_b = latest.get((_a = message.logical_key) !== null && _a !== void 0 ? _a : message.id)) === null || _b === void 0 ? void 0 : _b.id) === message.id; };
    return {
        failure: candidates.find(function (message) { return message.state === EMessageAttemptState.FAILED && isLatest(message); }),
        unconfirmed: candidates.find(function (message) { return message.state === EMessageAttemptState.UNKNOWN && isLatest(message); }),
    };
};
